/**
 * Workspace URL utilities
 * Handles URL generation for both private and public workspaces
 */

/**
 * Generate the appropriate workspace URL based on group visibility
 *
 * @param {Object} group - The group object
 * @param {string} group.slug - The group slug
 * @param {string} group.token - The group token/ID (used as workspaceId for public)
 * @param {boolean} group.isPublic - Whether the workspace is public
 * @returns {string} The workspace URL path
 *
 * @example
 * // Private workspace
 * getWorkspaceUrl({ slug: 'marketing-team', token: 'abc123', isPublic: false })
 * // Returns: '/teams/workspace/marketing-team'
 *
 * // Public workspace
 * getWorkspaceUrl({ slug: 'marketing-team', token: 'abc123', isPublic: true })
 * // Returns: '/teams/workspace/abc123/marketing-team'
 */
export function getWorkspaceUrl(group) {
  if (!group || !group.slug) {
    return '/teams/workspace'
  }

  // Check if workspace is public (using settings.isPrivate inverted)
  const isPublic =
    group.settings?.isPrivate === false || group.isPublic === true

  if (isPublic) {
    // Public workspace: /teams/workspace/{workspaceId}/{slug}
    const workspaceId = group.token || group.id
    return `/teams/workspace/${workspaceId}/${group.slug}`
  }

  // Private workspace: /teams/workspace/{slug}
  return `/teams/workspace/${group.slug}`
}

/**
 * Generate a full workspace URL with host
 *
 * @param {Object} group - The group object
 * @param {string} host - The host URL (e.g., process.env.VUE_APP_HOST)
 * @returns {string} The full workspace URL
 *
 * @example
 * getFullWorkspaceUrl(group, 'https://linkbook.io')
 * // Returns: 'https://linkbook.io/teams/workspace/marketing-team'
 */
export function getFullWorkspaceUrl(group, host = process.env.VUE_APP_HOST) {
  const path = getWorkspaceUrl(group)
  return `${host}${path}`
}

/**
 * Parse workspace URL parameters
 *
 * @param {Object} route - Vue Router route object
 * @returns {Object} Parsed parameters
 *
 * @example
 * parseWorkspaceUrl({ params: { slug: 'marketing-team' } })
 * // Returns: { slug: 'marketing-team', workspaceId: null, isPublicUrl: false }
 *
 * parseWorkspaceUrl({ params: { workspaceId: 'abc123', slug: 'marketing-team' } })
 * // Returns: { slug: 'marketing-team', workspaceId: 'abc123', isPublicUrl: true }
 */
export function parseWorkspaceUrl(route) {
  const { slug, workspaceId } = route.params || {}

  return {
    slug: slug || null,
    workspaceId: workspaceId || null,
    isPublicUrl: !!workspaceId
  }
}

/**
 * Check if a workspace URL is public format
 *
 * @param {string} path - The URL path
 * @returns {boolean} True if public format
 *
 * @example
 * isPublicWorkspaceUrl('/teams/workspace/abc123/marketing-team') // true
 * isPublicWorkspaceUrl('/teams/workspace/marketing-team') // false
 */
export function isPublicWorkspaceUrl(path) {
  // Public format: /teams/workspace/{workspaceId}/{slug}
  const publicPattern = /^\/teams\/workspace\/[^/]+\/[^/]+$/
  return publicPattern.test(path)
}

/**
 * Convert between private and public workspace URLs
 *
 * @param {Object} group - The group object
 * @param {boolean} makePublic - True to convert to public, false for private
 * @returns {string} The converted URL
 */
export function convertWorkspaceUrl(group, makePublic) {
  if (!group || !group.slug) {
    return '/teams/workspace'
  }

  if (makePublic) {
    const workspaceId = group.token || group.id
    return `/teams/workspace/${workspaceId}/${group.slug}`
  }

  return `/teams/workspace/${group.slug}`
}
